
import { Component, Model, Prop, Vue, Watch } from "vue-property-decorator";
import * as Models from "@gigalot/data-models";
import { formatDoseLabel as formatDoseLabelHelper } from "../helpers/doses";

@Component
export default class VaccinationSelectInput extends Vue {
  @Model("update:selectedVaccination", { type: Object }) readonly selectedVaccination!: Models.Vaccination | null;
  @Prop({ type: Array, default: () => [] }) readonly vaccinations!: Models.Vaccination[];
  @Prop({ type: Array, default: () => [] }) readonly appliedDoses!: Models.AppliedDose[];
  @Prop({ type: Boolean, default: false }) readonly showDoseDetails!: boolean;
  @Prop({ type: Boolean, default: false }) readonly alwaysRequireMass!: boolean;
  @Prop({ type: Boolean, default: true }) readonly massValid!: boolean;
  @Prop({ type: Boolean, default: false }) readonly useAverageMass!: boolean;
  @Prop({ type: String, default: "Vaccination" }) readonly label!: string;
  @Prop({ type: Boolean, default: true }) readonly isRequired!: boolean;
  @Prop({ type: String, default: "Select Vaccination" }) readonly dialogTitle!: string;
  @Prop({ type: String, default: "None selected" }) readonly emptyText!: string;
  @Prop({ type: [String, Number], default: 640 }) readonly maxWidth!: string | number;
  @Prop({ type: Boolean, default: false }) readonly isLocked!: boolean;

  dialog: boolean = false;
  search: string = "";
  detailsDialog: boolean = false;
  details: Models.Vaccination | null = null;
  draftVaccination: Models.Vaccination | null = null;
  draftVaccinationKey: string = "";

  get filteredVaccinations(): Models.Vaccination[] {
    const term = this.search.trim().toLowerCase();
    if (!term) return this.vaccinations;
    return this.vaccinations.filter((v) => (v.description || "").toLowerCase().includes(term));
  }

  doseKey(dose: Models.Dose, idx: number): string | number {
    return (dose.item && dose.item.description) || idx;
  }

  doseDescription(dose: Models.Dose, idx: number): string {
    return (dose.item && dose.item.description) || `Dose ${idx + 1}`;
  }

  formatDoseLabel(dose: Models.Dose): string {
    return formatDoseLabelHelper(dose);
  }

  openVaccinationDetails(vaccination: Models.Vaccination) {
    this.details = vaccination;
    this.detailsDialog = true;
  }

  closeVaccinationDetails() {
    this.detailsDialog = false;
    this.details = null;
  }

  openDialog() {
    if (this.isLocked) {
      return;
    }

    this.search = "";
    this.draftVaccination = this.selectedVaccination;
    this.draftVaccinationKey = this.vaccinationKey(this.draftVaccination);
    this.dialog = true;
  }

  closeDialog() {
    this.dialog = false;
    this.draftVaccination = this.selectedVaccination;
    this.draftVaccinationKey = this.vaccinationKey(this.draftVaccination);
  }

  @Watch("dialog")
  onDialogChanged(nextValue: boolean, previousValue: boolean) {
    if (previousValue && !nextValue) {
      this.$nextTick(() => requestAnimationFrame(() => (document.activeElement as HTMLElement | null)?.blur()));
    }
  }

  vaccinationKey(vaccination: Models.Vaccination | null): string {
    return (vaccination?.guid || vaccination?.description || "").toString();
  }

  isSelectedVaccination(vaccination: Models.Vaccination) {
    return this.vaccinationKey(this.draftVaccination) === this.vaccinationKey(vaccination);
  }

  selectVaccination(vaccination: Models.Vaccination) {
    this.draftVaccination = vaccination;
    this.draftVaccinationKey = this.vaccinationKey(vaccination);
  }

  @Watch("draftVaccinationKey")
  onDraftVaccinationKeyChanged(nextValue: string) {
    if (!nextValue) {
      this.draftVaccination = null;
      return;
    }
    const match = this.vaccinations.find((v) => this.vaccinationKey(v) === nextValue);
    if (match) this.draftVaccination = match;
  }

  clearVaccinationSelection() {
    this.$emit("update:selectedVaccination", null);
  }

  saveSelection() {
    this.$emit("update:selectedVaccination", this.draftVaccination ?? null);
    this.closeDialog();
  }

  get selectedVaccinationDoses(): Models.Dose[] {
    return this.selectedVaccination?.doses ?? [];
  }

  get shouldShowMassRequired(): boolean {
    return (this.alwaysRequireMass || this.useAverageMass) && !this.massValid;
  }

  get doseTypes(): { [doseDescription: string]: "cc-mass-dependent" | "constant" } {
    const ret: { [doseDescription: string]: "cc-mass-dependent" | "constant" } = {};
    for (const dose of this.selectedVaccinationDoses) {
      if (dose.item && dose.type) ret[dose.item.description] = dose.type;
    }
    return ret;
  }

  get appliedDosesMassDependent() {
    return this.appliedDoses.filter((ad) => this.doseTypes[ad.item.description] === "cc-mass-dependent");
  }

  get appliedDosesMassConstant() {
    return this.appliedDoses.filter((ad) => this.doseTypes[ad.item.description] === "constant");
  }

  formatAppliedDoseDetail(appliedDose: Models.AppliedDose): string {
    const matchingDose = this.selectedVaccinationDoses.find((d) => d.item?.description === appliedDose.item.description);
    if (!matchingDose) return "";
    return this.formatDoseLabel(matchingDose);
  }

  doseRowClass(overallIndex: number): string {
    return overallIndex % 2 === 1 ? "dose-row-alt" : "";
  }
}
